import type {
  GlobalAssistantCurrentPageContext,
  GlobalAssistantDiscoveryKind,
  GlobalAssistantEntityType,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';
import {
  isScreenerV2DashboardPath,
  isTalentDiscoveryPublicPath,
} from './global-ai-assistant-route-filter-kind';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

export type LastAppliedFilterContext = {
  prompt: string;
  targetPath: string;
  surfaceKind: 'discovery' | 'dashboard';
  discoveryKind?: GlobalAssistantDiscoveryKind;
  entityType?: GlobalAssistantEntityType;
};

const CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX = '__current__:apply-filters:';
const CUSTOM_DASH_CURRENT_PAGE_FILTER_PREFIX =
  '__current__:custom-dash-filter:';

export function isCustomDashCurrentPageFilterMatch(
  match: GlobalAssistantSuggestMatch
): boolean {
  return match.pageId.startsWith(CUSTOM_DASH_CURRENT_PAGE_FILTER_PREFIX);
}

export function isSamePageFilterApplyMatch(
  match: GlobalAssistantSuggestMatch,
  currentPathname: string
): boolean {
  return (
    isCurrentPageFilterApplyMatch(match) ||
    (isCustomDashCurrentPageFilterMatch(match) &&
      globalAssistantPathsMatch(currentPathname, match.navigatePath))
  );
}

export function isCurrentPageFilterApplyMatch(
  match: GlobalAssistantSuggestMatch
): boolean {
  return match.pageId.startsWith(CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX);
}

export function getAlreadyOnPageMessage(
  match?: GlobalAssistantSuggestMatch
): string {
  const pageName = match?.name.trim();
  if (pageName) {
    return `You're already on the ${pageName} page.`;
  }
  return "You're already on that page.";
}

/**
 * Returns true when the given match is a pure "self-link" navigation
 * suggestion — its `navigatePath` already matches the current page and it
 * carries no metadata (`resolvedPromptForApply` or
 * `applyFiltersAfterNavigate`) that would make the click do real work.
 * Synthetic current-page apply chips (which intentionally target the
 * current page to apply filters) are also excluded so they keep rendering.
 */
export function isSelfLinkNavigationMatch(
  match: GlobalAssistantSuggestMatch,
  currentPathname: string
): boolean {
  if (isCurrentPageFilterApplyMatch(match)) {
    return false;
  }
  if (match.resolvedPromptForApply?.trim()) {
    return false;
  }
  // `applyFiltersAfterNavigate` matches still apply discovery filters on
  // click using the user's last prompt (see
  // `getGlobalAssistantPageMatchAction` -> `apply_discovery_filters_after_navigate`),
  // so they are not pure self-links even when targeting the current page.
  if (match.applyFiltersAfterNavigate) {
    return false;
  }
  return globalAssistantPathsMatch(currentPathname, match.navigatePath);
}

/**
 * Drops matches that would render as a redundant self-link suggestion to
 * the current page (e.g. a "Compositions" chip while already on
 * `/company/compositions`). Filter-apply chips for the current page are
 * intentionally preserved so the user can still apply filters in place.
 */
export function filterOutSelfLinkNavigationMatches(
  matches: GlobalAssistantSuggestMatch[],
  currentPathname: string
): GlobalAssistantSuggestMatch[] {
  return matches.filter(
    (match) => !isSelfLinkNavigationMatch(match, currentPathname)
  );
}

function getPageNameFromPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return 'this page';
  }
  const [entity, page] = parts;
  const entityName = (() => {
    if (entity === 'company') return 'Company';
    if (entity === 'geography') return 'Geography';
    if (entity === 'role') return 'Role';
    return entity;
  })();
  if (page === 'screener') {
    return `${entityName} Discovery`;
  }
  if (page) {
    const pageName = page
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${entityName} ${pageName}`;
  }
  return 'this page';
}

export function getCurrentPageFilterApplyLabel(pathname: string): string {
  const pageName = getPageNameFromPath(pathname);
  return `Apply filters to ${pageName}`;
}

function looksLikeCustomDashPath(pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment === 'company' || segment === 'geography' || segment === 'role';
}

function isDataDictionaryPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  return parts[0] === 'data-dictionary';
}

function isNormalDashboardFilterPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  const [entity, page] = parts;
  if (page === undefined || page === 'screener') {
    return false;
  }
  return entity === 'company' || entity === 'geography' || entity === 'role';
}

function isCurrentPageFilterApplyPath(pathname: string): boolean {
  return (
    isNormalDashboardFilterPath(pathname) || isScreenerV2DashboardPath(pathname)
  );
}

/**
 * App root is the home/landing screen — the one place assistant
 * auto-navigation is allowed (nothing to yank the user away from). The single
 * definition of "home" for the assistant: the context builder, the navigate
 * client tool, and the drawer's auto-fire/chip-suppression logic all key off
 * this so they can never diverge.
 */
export function isAssistantHomePath(pathname: string): boolean {
  return pathname === '/';
}

export function buildGlobalAssistantCurrentPageContext(args: {
  pathname: string;
  isInCustomDashEditMode: boolean;
  lastAppliedFilterContext?: LastAppliedFilterContext;
}): GlobalAssistantCurrentPageContext {
  const normalizedPrompt =
    args.lastAppliedFilterContext?.prompt.trim() || undefined;

  if (isAssistantHomePath(args.pathname)) {
    return { pathname: args.pathname, kind: 'home' };
  }

  if (isDataDictionaryPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'other',
    };
  }

  if (isTalentDiscoveryPublicPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'discovery',
      discoveryKind: 'talent',
      ...(normalizedPrompt ? { baseDiscoveryPrompt: normalizedPrompt } : {}),
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
    };
  }

  if (isScreenerV2DashboardPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'discovery',
      discoveryKind: 'company',
      ...(normalizedPrompt ? { baseDiscoveryPrompt: normalizedPrompt } : {}),
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
    };
  }

  if (args.isInCustomDashEditMode || looksLikeCustomDashPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'custom_dash',
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
    };
  }

  return {
    pathname: args.pathname,
    kind: 'other',
  };
}

export function getDiscoveryApplyPrompt(
  match: GlobalAssistantSuggestMatch,
  fallbackPrompt?: string
): string | undefined {
  const override = match.resolvedPromptForApply?.trim();
  if (override) {
    return override;
  }
  const fallback = fallbackPrompt?.trim();
  return fallback || undefined;
}

export function shouldAutoApplyDiscoveryMatch(
  match: GlobalAssistantSuggestMatch,
  pathname: string
): boolean {
  return Boolean(
    match.discoveryKind &&
      match.resolvedPromptForApply?.trim() &&
      globalAssistantPathsMatch(pathname, match.navigatePath)
  );
}

export function shouldAutoApplyDashboardFilterMatch(
  match: GlobalAssistantSuggestMatch,
  pathname: string
): boolean {
  return Boolean(
    !match.discoveryKind &&
      match.resolvedPromptForApply?.trim() &&
      globalAssistantPathsMatch(pathname, match.navigatePath) &&
      (isCustomDashCurrentPageFilterMatch(match) ||
        isNormalDashboardFilterPath(match.navigatePath))
  );
}

function canApplyFiltersOnCurrentPage(args: {
  pathname: string;
  isInCustomDashEditMode?: boolean;
}): boolean {
  const ctx = buildGlobalAssistantCurrentPageContext({
    pathname: args.pathname,
    isInCustomDashEditMode: args.isInCustomDashEditMode ?? false,
  });
  return (
    ctx.kind === 'custom_dash' || isCurrentPageFilterApplyPath(args.pathname)
  );
}

function matchHasFilterApplyIntent(
  match: GlobalAssistantSuggestMatch
): boolean {
  if (match.applyFiltersAfterNavigate) {
    return true;
  }
  return Boolean(match.resolvedPromptForApply?.trim());
}

export function addCurrentPageFilterApplyOptionForDiscoveryMatch(args: {
  matches: GlobalAssistantSuggestMatch[];
  currentPathname: string;
  prompt?: string;
  isInCustomDashEditMode?: boolean;
}): GlobalAssistantSuggestMatch[] {
  const { matches, currentPathname, prompt, isInCustomDashEditMode } = args;
  if (matches.length !== 1) {
    return matches;
  }
  if (
    !canApplyFiltersOnCurrentPage({
      pathname: currentPathname,
      isInCustomDashEditMode,
    })
  ) {
    return matches;
  }

  const primaryMatch = matches[0];
  if (!primaryMatch || isCurrentPageFilterApplyMatch(primaryMatch)) {
    return matches;
  }
  if (globalAssistantPathsMatch(currentPathname, primaryMatch.navigatePath)) {
    return matches;
  }
  if (!matchHasFilterApplyIntent(primaryMatch)) {
    return matches;
  }

  const promptForApply = getDiscoveryApplyPrompt(primaryMatch, prompt);
  if (!promptForApply) {
    return matches;
  }

  return [
    {
      pageId: `${CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX}${currentPathname}`,
      name: getCurrentPageFilterApplyLabel(currentPathname),
      navigatePath: currentPathname,
      confidenceScore: primaryMatch.confidenceScore,
      resolvedPromptForApply: promptForApply,
    },
    primaryMatch,
  ];
}

export function hasCurrentPageFilterApplyOption(
  matches: GlobalAssistantSuggestMatch[]
): boolean {
  return matches.some(isCurrentPageFilterApplyMatch);
}

export function isGlobalAssistantCurrentPageFilterable(args: {
  pathname: string;
  isInCustomDashEditMode: boolean;
}): boolean {
  const ctx = buildGlobalAssistantCurrentPageContext({
    pathname: args.pathname,
    isInCustomDashEditMode: args.isInCustomDashEditMode,
  });
  return (
    ctx.kind === 'custom_dash' ||
    ctx.kind === 'discovery' ||
    isCurrentPageFilterApplyPath(args.pathname)
  );
}
